import { type RefObject, useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

import { currentIndex, readHistoryTrail } from 'hooks/useHistoryTrail';
import { MAIN_CONTENT_AREA_ID } from 'layout/MainLayout/useContentAreaRect';

/** How long an overlay the navigation started in has to close, exit animation included, for focus to follow it. */
const OVERLAY_CLOSE_MS = 1000;

/**
 * A page change leaves focus on the link that made it, or on nothing once that link is gone: it moves to the new
 * page's title, and to `<main>` until the page has rendered one, since `focus()` does nothing on a heading that is
 * not made focusable. A page is what the history trail calls one, recorded for this entry before this runs: the
 * tabs of a record, each a path of its own, are one page, and switching between them moves nothing. Not on a
 * `replace` either, which is a redirect; and not when focus is already in the page, on a field it focuses as it
 * opens.
 *
 * A navigation made from an overlay outside the frame (the rail's popover, the search dialog, the phone's drawer)
 * waits for that overlay to close, and moves focus after the overlay has returned it to its opener. An overlay
 * that stays open over the new page keeps its focus.
 *
 * Either wait outlives the navigations that are not a page change: a record redirects to its first tab as it
 * opens, before it has a title.
 */
export function usePageChangeFocus(mainRef: RefObject<HTMLElement | null>) {
    const { pathname } = useLocation();
    const navigationType = useNavigationType();
    const landedOn = useRef<string | null>(null);
    const stopWaiting = useRef(() => {});

    useEffect(() => () => stopWaiting.current(), []);

    useEffect(() => {
        const page = readHistoryTrail()[currentIndex()]?.page ?? pathname;
        const samePage = landedOn.current === null || landedOn.current === page;
        landedOn.current = page;
        const main = mainRef.current;
        const focused = document.activeElement;
        if (samePage || !main || navigationType === 'REPLACE') return;
        stopWaiting.current();

        let timer: number | undefined;
        const title = () => main.querySelector<HTMLElement>('h1[tabindex]');
        const titleArrived = new MutationObserver(() => {
            // Focus the user has moved off `<main>` meanwhile stays where they put it.
            if (document.activeElement !== main) titleArrived.disconnect();
            else title()?.focus({ preventScroll: true });
        });
        const land = () => {
            if (document.getElementById(MAIN_CONTENT_AREA_ID)?.contains(document.activeElement)) return;
            const heading = title();
            (heading ?? main).focus({ preventScroll: true });
            if (!heading) titleArrived.observe(main, { childList: true, subtree: true });
        };

        const inOverlay = focused && focused !== document.body && !main.parentElement?.contains(focused);
        const overlayGone = new MutationObserver(() => {
            if (focused?.isConnected) return;
            overlayGone.disconnect();
            window.clearTimeout(timer);
            // A task later: the overlay returns focus to its opener in a microtask of its unmount.
            timer = window.setTimeout(land);
        });
        if (inOverlay) {
            overlayGone.observe(document.body, { childList: true, subtree: true });
            timer = window.setTimeout(() => overlayGone.disconnect(), OVERLAY_CLOSE_MS);
        } else {
            land();
        }
        stopWaiting.current = () => {
            window.clearTimeout(timer);
            overlayGone.disconnect();
            titleArrived.disconnect();
        };
    }, [mainRef, pathname, navigationType]);
}
